import { useRef, useEffect, useState } from 'react';
import { Zap, Shield, Smartphone, Users, ArrowRight } from 'lucide-react';

const solutions = [
  {
    id: 'energia',
    icon: Zap,
    number: '01',
    title: 'Economia na Energia',
    description:
      'Reduza sua conta de energia sem precisar instalar placas, fazer obras ou investir. Economia garantida por geração compartilhada.',
    cta: 'Quero saber mais',
    ctaUrl:
      'https://wa.me/5551996777625?text=Ol%C3%A1!%20Gostaria%20de%20verificar%20se%20posso%20economizar%20na%20minha%20conta%20de%20energia.%20Quero%20falar%20com%20um%20especialista%20iGreen.',
    accent: 'primary',
  },
  {
    id: 'seguro',
    icon: Shield,
    number: '02',
    title: 'Seguro Veicular',
    description:
      'Proteção completa para o seu veículo com planos flexíveis, pagamento mensal e atendimento ágil em todo o Brasil.',
    cta: 'Fazer uma cotação',
    ctaUrl:
      'https://wa.me/5551996777625?text=Ol%C3%A1!%20Gostaria%20de%20fazer%20uma%20cota%C3%A7%C3%A3o%20para%20o%20meu%20ve%C3%ADculo%20e%20conhecer%20tamb%C3%A9m%20o%20cashback%20para%20aumentar%20meu%20desconto%20na%20prote%C3%A7%C3%A3o%20veicular.',
    accent: 'accent',
  },
  {
    id: 'telecom',
    icon: Smartphone,
    number: '03',
    title: 'iGreen Telecom',
    description:
      'Planos de telefonia com internet de alta velocidade, dados que acumulam, atendimento 24h e sem fidelidade.',
    cta: 'Conhecer os planos',
    ctaUrl:
      'https://wa.me/5551996777625?text=Ol%C3%A1!%20Gostaria%20de%20conhecer%20os%20planos%20da%20iGreen%20Telecom%20e%20verificar%20a%20disponibilidade%20para%20minha%20regi%C3%A3o.',
    accent: 'secondary',
  },
  {
    id: 'licenciado',
    icon: Users,
    number: '04',
    title: 'Seja um Licenciado',
    description:
      'Conecte clientes aos serviços iGreen e desenvolva uma fonte de renda recorrente com treinamentos e suporte.',
    cta: 'Conhecer a oportunidade',
    ctaUrl:
      'https://wa.me/5551996777625?text=Ol%C3%A1!%20Tenho%20interesse%20em%20me%20tornar%20um%20licenciado%20iGreen%20e%20gostaria%20de%20entender%20como%20funciona%20a%20oportunidade%20e%20a%20renda%20recorrente.',
    accent: 'primary',
  },
];

const accentMap: Record<string, { badge: string; iconBg: string; iconText: string; borderHover: string; glow: string }> = {
  primary: {
    badge: 'bg-primary-500/10 text-primary-600 border-primary-500/20',
    iconBg: 'bg-primary-500/10',
    iconText: 'text-primary-500',
    borderHover: 'hover:border-primary-500/50',
    glow: 'hover:shadow-primary-500/10',
  },
  accent: {
    badge: 'bg-accent-500/10 text-accent-600 border-accent-500/20',
    iconBg: 'bg-accent-500/10',
    iconText: 'text-accent-500',
    borderHover: 'hover:border-accent-500/50',
    glow: 'hover:shadow-accent-500/10',
  },
  secondary: {
    badge: 'bg-secondary-500/10 text-secondary-600 border-secondary-500/20',
    iconBg: 'bg-secondary-500/10',
    iconText: 'text-secondary-500',
    borderHover: 'hover:border-secondary-500/50',
    glow: 'hover:shadow-secondary-500/10',
  },
};

function Card({ solution, index }: { solution: typeof solutions[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const accent = accentMap[solution.accent] || accentMap.primary;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`group relative flex h-full flex-col items-center text-center sm:items-start sm:text-left bg-background-50 rounded-2xl border border-background-200/70 p-5 md:p-6 transition-all duration-500 ${accent.borderHover} hover:-translate-y-1 hover:shadow-xl ${accent.glow} ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {/* Number badge */}
      <span
        className={`inline-flex self-center sm:self-start items-center px-3 py-1 rounded-full text-xs font-bold border mb-4 ${accent.badge}`}
      >
        {solution.number}
      </span>

      {/* Icon */}
      <div
        className={`w-16 h-16 rounded-2xl ${accent.iconBg} ${accent.iconText} ring-1 ring-inset ring-current/10 flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110`}
      >
        <solution.icon className="w-8 h-8" />
      </div>

      {/* Title */}
      <h3 className="font-heading text-xl font-bold text-foreground-950 mb-2 tracking-tight">
        {solution.title}
      </h3>

      {/* Description */}
      <p className="flex-1 text-sm md:text-base text-foreground-600 leading-relaxed mb-5">
        {solution.description}
      </p>

      {/* CTA */}
      <a
        href={solution.ctaUrl}
        target="_blank"
        rel="noreferrer"
        className={`inline-flex items-center justify-center sm:justify-start gap-2 text-sm font-semibold ${accent.iconText} group/link`}
      >
        {solution.cta}
        <ArrowRight className="w-4 h-4 transition-transform group-hover/link:translate-x-1" />
      </a>

      {/* Corner accent line */}
      <div
        className={`absolute top-0 left-8 right-8 h-[2px] rounded-full ${accent.iconBg.replace('/10', '/0')} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
        style={{ backgroundColor: 'currentColor' }}
      />
    </div>
  );
}

export default function SolutionCards() {
  return (
    <section id="solucoes" className="immersive-panel bg-background-50">
      <div className="immersive-inner">
        {/* Section header */}
        <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-100 border border-primary-200 text-primary-700 text-sm font-semibold mb-4">
            O Ecossistema iGreen
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 tracking-tighter leading-[1.1]">
            O que você procura na<br />{' '}
            <span className="text-gradient">iGreen Energy</span>?
          </h2>
          <p className="mt-4 text-lg text-foreground-600">
            Escolha uma solução e fale com um licenciado autorizado.
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {solutions.map((solution, i) => (
            <Card key={solution.id} solution={solution} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
